'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Menu, X } from 'lucide-react';
import AshaIcon from '@/components/ui/AshaIcon';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Services', href: '/services' },
  { label: 'Locations', href: '/locations' },
  { label: 'Resources', href: '/resources' },
  { label: 'Referrals', href: '/referrals' },
  { label: 'Contact', href: '/contact' },
];

export default function PublicNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-border'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link href="/" className="flex items-center flex-shrink-0">
            <img
              src="/assets/images/LOGOHORIZINTAL-1777629360782.png"
              alt="ASHA Recovery Group"
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks?.map((link) => (
              <Link
                key={`nav-${link?.label}`}
                href={link?.href}
                className={`px-3.5 py-2 text-sm font-medium rounded-lg hover:bg-accent transition-all duration-150 ${
                  scrolled
                    ? 'text-foreground/80 hover:text-primary'
                    : 'text-white hover:text-white/80'
                }`}
              >
                {link?.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              href="/sign-up-login-screen"
              className="px-4 py-2 text-sm font-semibold text-primary border border-primary/30 rounded-xl hover:bg-accent transition-all duration-150 flex items-center gap-2"
            >
              <AshaIcon name="patient-portal" size={16} color="var(--primary)" />
              Patient Portal
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="btn-primary text-sm px-4 py-2 flex items-center gap-2"
            >
              <AshaIcon name="find-clinic" size={16} color="white" />
              Clinic Login
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-xl text-foreground hover:bg-accent transition-colors"
            aria-label="Toggle navigation menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {/* Mobile Drawer */}
      <div
        className={`lg:hidden transition-all duration-300 overflow-hidden ${
          mobileOpen ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-white border-t border-border px-6 py-4 space-y-1">
          {navLinks?.map((link) => (
            <Link
              key={`mobile-nav-${link?.label}`}
              href={link?.href}
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2.5 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-accent rounded-xl transition-all duration-150"
            >
              {link?.label}
            </Link>
          ))}
          <div className="pt-3 border-t border-border flex flex-col gap-2">
            <Link
              href="/sign-up-login-screen"
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-primary border border-primary/30 rounded-xl hover:bg-accent transition-all duration-150"
            >
              <AshaIcon name="patient-portal" size={16} color="var(--primary)" />
              Patient Portal
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="btn-primary justify-center flex items-center gap-2"
            >
              <AshaIcon name="find-clinic" size={16} color="white" />
              Clinic Login
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}